import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ArrowBack, FileDownload } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { useInvoiceQuery } from '../../../api/gql';
import { ExpandedBox } from '../../../components/ExpandedBox';
import { FullSizeLoader } from '../../../components/FullSizeLoader';
import { InvoiceControlBox } from '../../../components/invoice/InvoiceControlBox';
import { InvoiceScheduledActionNote } from '../../../components/invoice/InvoiceScheduledActionNote';
import { PageHeader } from '../../../components/PageHeader';
import { RemindingToneInfoBox } from '../../../components/RemindingToneInfoBox';
import { useSafeNavigation } from '../../../hooks/useSafeNavigation';
import { PageContent, PageLayout } from '../../../layout';
import { InvoiceForm } from '../../../modules';
import { CustomerContactForm } from '../../../modules/forms/customer/CustomerContactForm';
import type { InvoiceFormType } from '../../../modules/forms/invoice/types';
import { InvoiceArchiveButton } from '../../../modules/invoice/InvoiceArchiveButton';
import { InvoiceDownloadButton } from '../../../modules/invoice/pdf/InvoiceDownloadButton';
import { InvoicePdfData } from '../../../modules/invoice/pdf/InvoicePdfDocument';
import { getConfig } from '../../../utils/config';
import { currencies, getPriceWithCurrencyString } from '../../../utils/currencies';
import { formatDate } from '../../../utils/format';
import { notify } from '../../../utils/notification';
import { replaceNullOrUndefined } from '../../../utils/object';
import { fromCentesimal } from '../../../utils/transform';
import { ScheduledActionsTableBox } from './modules/InvoiceScheduledActionsBox';

const TRANS_NS = 'page-invoice-detail';
const { gqlApiEndpoint } = getConfig();

export const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const { t } = useTranslation(TRANS_NS);
  const { safeNavigate } = useSafeNavigation();

  const { control, reset, setValue } = useForm<InvoiceFormType>();
  const { data, isLoading, isError } = useInvoiceQuery({
    id: invoiceId ?? '',
  });

  const pdfData = useMemo<InvoicePdfData | null>(() => {
    const invoice = data?.invoice;
    if (!invoice) return null;

    return {
      ...invoice,
      amount: getPriceWithCurrencyString(fromCentesimal(invoice.amount ?? 0), invoice.currency),
      currency: currencies.find((curr) => curr.value === invoice.currency?.toLowerCase())?.label ?? '-',
      issueDate: invoice.issueDate ? formatDate(new Date(invoice.issueDate)) : null,
      dueDate: invoice.dueDate ? formatDate(new Date(invoice.dueDate)) : null,
    };
  }, [data?.invoice]);

  useEffect(() => {
    if (!data?.invoice) {
      return;
    }
    //set invoice form data on query data
    reset(
      replaceNullOrUndefined({
        ...data.invoice,
        amount: fromCentesimal(data.invoice.amount ?? 0),
        amountDue: fromCentesimal(data.invoice.amountDue ?? 0),
        currency: data.invoice.currency?.toLowerCase(),
      }),
    );
  }, [data?.invoice, reset]);

  const hasBONote = useMemo(
    () => data?.invoice?.scheduledActions?.some((a) => a.note) ?? false,
    [data?.invoice?.scheduledActions],
  );

  const onArchived = (failed?: string[]) => {
    if (failed?.length) {
      notify.negative({ message: t('archive.failed') });
      return;
    }

    notify.positive({ message: t('archive.success') });
    safeNavigate('/invoices');
  };
  const onArchiveError = () => {
    notify.negative({ message: t('archive.failed') });
  };

  if (isLoading) {
    return <FullSizeLoader />;
  }

  return (
    <>
      <PageLayout>
        <PageHeader
          title={`${t('title')}`}
          subtitle={data?.invoice.number}
          Icon={ArrowBack}
          action={
            invoiceId && data?.invoice ? (
              <Stack direction={'row'} gap={2} alignItems={'center'}>
                {data?.invoice.canBeArchived ? (
                  <InvoiceArchiveButton invoiceIds={[invoiceId]} onArchived={onArchived} onError={onArchiveError} />
                ) : null}
                <InvoiceDownloadButton data={pdfData} />
                {data?.invoice.downloadUrl ? (
                  <DownloadInvoiceFileButton downloadUrl={data?.invoice.downloadUrl} />
                ) : null}
              </Stack>
            ) : null
          }
        />
        <PageContent
          sx={{
            flexDirection: 'column',
            gap: 4,
            justifyContent: 'flex-start',
          }}
        >
          {data?.invoice ? <InvoiceControlBox invoice={data.invoice} /> : null}
          <Box width={'100%'} display={'flex'} flexWrap={'wrap'} gap={2}>
            <Box flex={1} width={'100%'}>
              <Typography variant={'h3'} mb={2}>
                {t('scheduledActions.title')}
              </Typography>
              <ScheduledActionsTableBox
                scheduledActions={data?.invoice.scheduledActions}
                isLoading={isLoading}
                isError={isError}
                transNS={TRANS_NS}
              />
            </Box>
            {hasBONote ? (
              <Box flex={1}>
                <Typography variant={'h3'} mb={2}>
                  {t('operatorNotes')}
                </Typography>
                <Stack gap={1}>
                  {data?.invoice?.scheduledActions?.map((scheduledAction) => (
                    <InvoiceScheduledActionNote key={scheduledAction.id} scheduledAction={scheduledAction} />
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Box>
          <Box display={'flex'} flexWrap={'wrap'} gap={2}>
            <Box
              flex={2}
              minWidth={{
                xs: 280,
                md: 724,
              }}
            >
              <Typography variant={'h3'} mb={2}>
                {t('invoiceInfo')}
              </Typography>

              <Paper variant={'outlined'} sx={{ flex: 1, p: 4, position: 'relative' }}>
                <InvoiceForm
                  readonly={true}
                  control={control}
                  setValue={setValue}
                  highlightAreas={false}
                  selectedCurrency={data?.invoice.currency}
                  allowInvoiceItems={false}
                />
                {isError ? (
                  <ExpandedBox>
                    <Typography color={'text.secondary'}>{t('error.query')}</Typography>
                  </ExpandedBox>
                ) : null}
              </Paper>
            </Box>
            <Box flex={1}>
              <Typography variant={'h3'} mb={2}>
                {t('customerContact')}
              </Typography>
              <Paper sx={{ p: 4, position: 'relative', minHeight: 175 }}>
                {data?.invoice.customer && invoiceId ? (
                  <CustomerContactForm
                    invoiceId={invoiceId}
                    id={data.invoice.customer.id}
                    vatId={data.invoice.customer.vatId}
                    name={data.invoice.customer.name}
                    email={data.invoice.customer.email}
                    phoneNumber={data.invoice.customer.phoneNumber}
                  />
                ) : null}
                {isError ? (
                  <ExpandedBox>
                    <Typography color={'text.secondary'}>{t('error.query')}</Typography>
                  </ExpandedBox>
                ) : null}
              </Paper>
              {data?.invoice.remindingTone ? (
                <RemindingToneInfoBox tone={data.invoice.remindingTone} sx={{ mt: 4 }} />
              ) : null}
            </Box>
          </Box>
        </PageContent>
      </PageLayout>
      {/*OUTLET for modal children */}
      <Outlet />
    </>
  );
};

const DownloadInvoiceFileButton = (props: { downloadUrl: string }) => {
  const endpoint = gqlApiEndpoint.replace('/graphql', props.downloadUrl);
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);

  const onClick = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();

      const res = await fetch(endpoint, {
        method: 'GET',
        mode: 'cors',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const blob = await res.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'upominej-faktura.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  return (
    <LoadingButton loading={loading} startIcon={<FileDownload />} variant={'outlined'} onClick={onClick}>
      Stáhnout originál
    </LoadingButton>
  );
};
